import { useCallback, useEffect, useState } from "react";
import { filter } from "rxjs/operators";
import { Connectable } from "rxjs";
import { emergencyContactService } from "services/emergencyContactService";
import { emergencyContactInfo$ } from "services/subjects";
import type { Relationship } from "interfaces/api/emergency";

type EmergencyContact = {
  relationship?: string;
  fullName?: string;
  phone?: string;
  email?: string;
};

type RelationshipsResponse = {
  relationships: Relationship[];
};

type UseEmergencyContactResult = {
  relationships: Relationship[];
  emergencyContact: EmergencyContact | null;
  fetched: boolean;
  reload: () => void;
};

export const useEmergencyContact = (): UseEmergencyContactResult => {
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [emergencyContact, setEmergencyContact] = useState<EmergencyContact | null>(null);
  const [fetched, setFetched] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const sub = emergencyContactService.getRelationships$
      .pipe(filter(Boolean))
      .subscribe((data: unknown) => {
        const response = data as RelationshipsResponse;
        setRelationships(response.relationships ?? []);
        setFetched(true);
      });

    const connection = (
      emergencyContactService.getRelationships$ as unknown as Connectable<unknown>
    ).connect();

    return () => {
      sub.unsubscribe();
      connection.unsubscribe();
    };
  }, [reloadKey]);

  useEffect(() => {
    const sub = emergencyContactInfo$
      .pipe(filter(Boolean))
      .subscribe((contact) => {
        setEmergencyContact(contact as EmergencyContact);
      });

    return () => sub.unsubscribe();
  }, []);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { relationships, emergencyContact, fetched, reload };
};
